import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { message } from "antd";

import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import Breadcrumb from "../../components/Common/Breadcrumb";
import ImageUploadModal from "../../components/Gallery/ImageUploadModal";
import DeleteModal from "../../components/Common/ImgDelete";

import { fetchAlbumById, fetchImagesByAlbumId, deleteImageById } from "../../services/galleryService";
import { trashicon } from "../../components/imagepath";
import { decrypt } from "../../utils/encryption";
import { BASE_URL_IMAGE } from "../../config/api";

const GalleryDetailsPage = () => {
  const { id: encodedId } = useParams();
  const albumId = decrypt(encodedId);
  const navigate = useNavigate();

  const [album, setAlbum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [deleteImage, setDeleteImage] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    const fetchAlbum = async () => {
      try {
        const albumData = await fetchAlbumById(albumId);
        const imageData = await fetchImagesByAlbumId(albumId);

        const processedImages = imageData.map(img => ({
          id: img.image_id,
          name: img.image_name,
          url: `${BASE_URL_IMAGE}/uploads/galleryImages/${img.image_name}`,
          size: 0,
          uploadedAt: new Date().toISOString(),
        }));

        setAlbum({ ...albumData, images: processedImages });
      } catch (err) {
        console.error(err);
        message.error("Failed to load album or images");
        navigate("/gallery");
      } finally {
        setLoading(false);
      }
    };

    fetchAlbum();
  }, [albumId, navigate]);

  const handleUpload = (albumId, newImages) => {
    setAlbum((prev) => ({
      ...prev,
      images: [...(prev.images || []), ...newImages],
    }));
    message.success(`${newImages.length} image(s) uploaded successfully.`);
    setShowUploadModal(false);
  };

  const confirmDeleteImage = async () => {
    if (!deleteImage) return;

    try {
      await deleteImageById(deleteImage.id);
      setAlbum((prev) => ({
        ...prev,
        images: prev.images.filter((img) => img.id !== deleteImage.id),
      }));
      message.success("Image deleted successfully.");
    } catch (error) {
      console.error("Delete error:", error);
      message.error("Failed to delete image.");
    }

    setDeleteImage(null);
    setShowDeleteModal(false);
  };

  if (loading) return <div className="text-center p-5">Loading album...</div>;
  if (!album) return <div className="text-center p-5">Album not found.</div>;

  const breadcrumbItems = [
    { label: "Gallery", path: "/gallery" },
    { label: album.album_name },
  ];

  return (
    <div>
      <Helmet>
        <title>{`${album.album_name || "Gallery Album"} | CRM JNEC`}</title>
      </Helmet>

      <Header />
      <Sidebar />

      <div className="page-wrapper">
        <div className="content">
          <Breadcrumb items={breadcrumbItems} />

          <div className="d-flex justify-content-between align-items-center mb-4">
            <h3>
              <span style={{ color: album.album_textColor }}>{album.album_icon}</span>{" "}
              {album.album_name} <small className="text-muted">({album.album_year})</small>
            </h3>
            <button className="btn btn-primary" onClick={() => setShowUploadModal(true)}>
              Upload Images
            </button>
          </div>

          {album.images?.length > 0 ? (
            <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 g-4">
              {album.images.map((img) => (
                <div className="col" key={img.id}>
                  <div className="card h-100 position-relative">
                    <img
                      src={img.url}
                      className="card-img-top"
                      alt={img.name}
                      style={{ height: "200px", objectFit: "cover" }}
                    />
                    <div className="card-body">
                      <h6 className="card-title text-truncate">{img.name}</h6>
                      <small className="text-muted">
                        {(img.size / 1024).toFixed(1)} KB •{" "}
                        {new Date(img.uploadedAt).toLocaleDateString()}
                      </small>
                    </div>
                    <button
                      className="btn btn-sm btn-outline-danger position-absolute"
                      style={{ top: "10px", right: "10px", background: "white" }}
                      onClick={() => {
                        setDeleteImage(img);
                        setShowDeleteModal(true);
                      }}
                    >
                      <img src={trashicon} alt="Delete" style={{ width: "16px", height: "16px" }} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-5">
              <h5>No images in this album</h5>
              <p className="text-muted">Upload some images to get started</p>
              <button
                className="btn btn-primary mt-2"
                onClick={() => setShowUploadModal(true)}
                style={{
                  backgroundColor: album.album_borderColor,
                  borderColor: album.album_borderColor,
                }}
              >
                Upload Images
              </button>
            </div>
          )}
        </div>
      </div>

      <ImageUploadModal
        isVisible={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        album={album}
        onUpload={handleUpload}
      />

      <DeleteModal
        isVisible={showDeleteModal}
        title="Delete Image"
        description={`Are you sure you want to delete "${deleteImage?.name}"?`}
        onCancel={() => {
          setDeleteImage(null);
          setShowDeleteModal(false);
        }}
        onConfirm={confirmDeleteImage}
      />
    </div>
  );
};

export default GalleryDetailsPage;
